import type { OrderModel } from 'src/types/order';

import dayjs from 'dayjs';

import type { OrderItem, BillingSchemaType, NewOrderSchemaType } from '../types/order-form.types';

// =============================================
// Helper: Extract platform ID from various formats
// =============================================

export function getCorrectPlatformId(platformData: any): number {
  if (typeof platformData === 'number') return platformData;

  if (platformData && typeof platformData === 'object') {
    if (platformData.value !== undefined) return Number(platformData.value);
    if (platformData.id !== undefined) return Number(platformData.id);
    if (typeof platformData === 'object' && Object.keys(platformData).length === 0) return 0;

    const possibleIdProps = ['_id', 'platformId', 'plataformaId', 'platform_id', 'plataforma_id'];
    for (const prop of possibleIdProps) {
      if (platformData[prop] !== undefined) return Number(platformData[prop]);
    }

    if (!Number.isNaN(Number(platformData))) return Number(platformData);
  }

  console.error(
    'No se pudo extraer el ID de la plataforma:',
    typeof platformData,
    platformData ? Object.keys(platformData) : 'null/undefined'
  );
  return 0;
}

// =============================================
// File to Base64
// =============================================

export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

// =============================================
// Map form data to OrderModel for UPDATE
// =============================================

export function buildUpdatePayload(
  currentOrder: OrderModel,
  formData: NewOrderSchemaType,
  billingData: BillingSchemaType,
  billingMethod: 'email' | 'platform'
): OrderModel {
  const updatedMediaId = formData.mediaId?.value;

  const updatedOrderData: OrderModel = {
    ...currentOrder,

    // Basic fields
    price: formData.price,
    currency: formData.currency,
    limitDate: formData.limitDate ? dayjs(formData.limitDate).toDate() : currentOrder.limitDate,
    finalUrl: formData.finalUrl,
    costPrice: formData.costPrice,
    costPriceCurrency: formData.costPriceCurrency,
    discountPercentage: formData.discountPercentage,
    internalPlatformId: formData.internalPlatformId,
    redactionUrl: formData.redactionUrl,

    // CRITICAL: Always preserve invoiceId from the original order
    invoiceId: currentOrder.invoiceId,

    // Billing method fields
    ...(billingMethod === 'platform'
      ? {
          platformId: getCorrectPlatformId(billingData.platformId),
          platform: billingData.platformId?.label || currentOrder.platform,
          // Only clear invoice data if the order does NOT have an existing invoiceId
          ...(currentOrder.invoiceId
            ? {} // Preserve invoice when order already has a real invoice
            : { invoice: undefined }),
        }
      : {
          platformId: undefined,
          platform: undefined,
          invoice: currentOrder.invoiceId
            ? currentOrder.invoice // Preserve original invoice data
            : {
                id: billingData.customerId || 0,
                name: billingData.firstName || '',
                surname: billingData.lastName || '',
                company: billingData.company || '',
                phone: billingData.phone || '',
                nif: billingData.nif || '',
                country: billingData.country || '',
                city: billingData.city || '',
                postalCode: billingData.postalCode || '',
                address: billingData.address || '',
              },
        }),

    // Update normalDetails
    normalDetails: currentOrder.normalDetails
      ? {
          ...currentOrder.normalDetails,
          mediaId: updatedMediaId ?? currentOrder.normalDetails.mediaId,
          categoryId: formData.categoryId || currentOrder.normalDetails.categoryId,
          extraSensibleCategory: formData.categoryCommission || 0,
          requireRedaction: formData.requireRedaction,
          wordsWrite: formData.wordCount || 600,
          orderDetail: formData.orderDetail,
          extraH1: formData.title,
          extraTermsToAvoid: formData.extraTermsToAvoid,
          extraHtml: formData.extraHtml,
          extraSource: formData.extraSource,
          url1: formData.url1,
          keyword1: formData.keyword1,
          linkType1: formData.linkType1,
          url2: formData.url2,
          keyword2: formData.keyword2,
          linkType2: formData.linkType2,
          url3: formData.url3,
          keyword3: formData.keyword3,
          linkType3: formData.linkType3,
          url4: formData.url4,
          keyword4: formData.keyword4,
          linkType4: formData.linkType4,
          externalEmail: billingMethod === 'email' ? billingData.email : undefined,
        }
      : undefined,
  };

  // Calculate total price
  const basePrice = formData.price;
  const categoryCommission = formData.categoryCommission || 0;
  const discountPercentage = formData.discountPercentage || 0;
  const priceAfterDiscount = basePrice * (1 - discountPercentage / 100);
  updatedOrderData.totalPrice = priceAfterDiscount + categoryCommission;

  return updatedOrderData;
}

// =============================================
// Map OrderItems to create request payload
// =============================================

export function buildCreatePayload(
  orderItems: OrderItem[],
  billingData: any,
  billingMethod: 'email' | 'platform',
  paymentMethodBackend: string,
  createdById?: number
) {
  const orderItemsFormatted = orderItems.map((item) => {
    const baseItem = {
      mediaId: item.mediaId,
      price: item.price,
      currency: item.currency,
      limitDate: item.limitDate || new Date(),
      categoryId: item.categoryId || 1,
      finalUrl: item.finalUrl,
      requireRedaction: item.requireRedaction,
      wordCount: item.wordCount,
      categoryCommission: item.categoryCommission || 0,
    };

    const linkData = item.requireRedaction
      ? {
          url1: item.url1,
          keyword1: item.keyword1,
          linkType1: item.linkType1,
          ...(item.url2 && { url2: item.url2 }),
          ...(item.keyword2 && { keyword2: item.keyword2 }),
          ...(item.linkType2 !== undefined && { linkType2: item.linkType2 }),
          ...(item.url3 && { url3: item.url3 }),
          ...(item.keyword3 && { keyword3: item.keyword3 }),
          ...(item.linkType3 !== undefined && { linkType3: item.linkType3 }),
          ...(item.url4 && { url4: item.url4 }),
          ...(item.keyword4 && { keyword4: item.keyword4 }),
          ...(item.linkType4 !== undefined && { linkType4: item.linkType4 }),
        }
      : {
          url1: null,
          keyword1: null,
          linkType1: null,
        };

    return {
      ...baseItem,
      ...linkData,
      orderDetail: item.orderDetail,
      extraTitle: item.title,
      extraTermsToAvoid: item.extraTermsToAvoid,
      extraHtml: item.extraHtml,
      extraSource: item.extraSource,
      fileBase64: item.fileBase64,
      imageBase64: item.imageBase64,
      imageTwoBase64: item.imageTwoBase64,
      costPrice: item.costPrice,
      costPriceCurrency: item.costPriceCurrency,
      internalPlatformId: item.internalPlatformId,
      discountPercentage: item.discountPercentage,
      directProfit: item.directProfit,
      redactionUrl: item.redactionUrl,
    };
  });

  return {
    orderItems: orderItemsFormatted,
    billingMethod,
    paymentMethod: paymentMethodBackend,
    createdById,
    ...(billingMethod === 'platform'
      ? { platformId: getCorrectPlatformId(billingData.platformId) }
      : { billingDetails: billingData }),
  };
}

// =============================================
// Map form submit data to OrderItem
// =============================================

export async function buildOrderItemFromForm(
  data: NewOrderSchemaType,
  medias: any[]
): Promise<OrderItem> {
  const selectedMediaId = data.mediaId?.value;
  const mediaFromOptions = medias?.find((media) => media.id === selectedMediaId);
  const mediaName = data.mediaId?.label || mediaFromOptions?.name || 'Medio sin nombre';

  let costPrice = data.costPrice || undefined;
  if (!costPrice && mediaFromOptions?.costPrice) {
    costPrice = mediaFromOptions.costPrice;
  }

  const categoryCommission = data.categoryCommission || 0;

  let fileBase64 = null;
  let imageBase64 = null;
  let imageTwoBase64 = null;

  if (data.file && data.file instanceof File) {
    try {
      fileBase64 = await fileToBase64(data.file);
    } catch (error) {
      console.error('Error al convertir archivo a base64:', error);
    }
  }
  if (data.image && data.image instanceof File) {
    try {
      imageBase64 = await fileToBase64(data.image);
    } catch (error) {
      console.error('Error al convertir imagen a base64:', error);
    }
  }
  if (data.imageTwo && data.imageTwo instanceof File) {
    try {
      imageTwoBase64 = await fileToBase64(data.imageTwo);
    } catch (error) {
      console.error('Error al convertir imagen extra a base64:', error);
    }
  }

  return {
    mediaName,
    price: data.price,
    currency: data.currency,
    finalUrl: data.finalUrl || '',
    wordCount: data.wordCount || 600,
    costPrice,
    costPriceCurrency: data.costPriceCurrency || data.currency,
    redactionPrice: 0,
    categoryCommission,
    fileBase64,
    imageBase64,
    imageTwoBase64,
    mediaId: selectedMediaId ?? null,
    categoryId: data.categoryId || null,
    limitDate: data.limitDate ? dayjs(data.limitDate).toDate() : new Date(),
    requireRedaction: data.requireRedaction === true,
    ...(data.requireRedaction === true
      ? { url1: data.url1 || '', keyword1: data.keyword1 || '', linkType1: data.linkType1 || 0 }
      : {
          url1: data.url1 || undefined,
          keyword1: data.keyword1 || undefined,
          linkType1: data.linkType1 || undefined,
        }),
    url2: data.url2,
    keyword2: data.keyword2,
    linkType2: data.linkType2,
    url3: data.url3,
    keyword3: data.keyword3,
    linkType3: data.linkType3,
    url4: data.url4,
    keyword4: data.keyword4,
    linkType4: data.linkType4,
    orderDetail: data.orderDetail,
    title: data.title,
    extraTermsToAvoid: data.extraTermsToAvoid,
    extraHtml: data.extraHtml,
    extraSource: data.extraSource,
    internalPlatformId: data.internalPlatformId,
    directProfit: data.directProfit,
    redactionUrl: data.redactionUrl,
    discountPercentage: data.discountPercentage,
  };
}

// =============================================
// Map payment method enum to backend string
// =============================================

export function mapPaymentMethodToBackend(paymentMethod: string): string {
  switch (paymentMethod) {
    case 'Paypal':
      return 'Paypal';
    case 'Transfer':
      return 'Transfer';
    default:
      return 'Pending';
  }
}
